import { type ReactElement, useCallback } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { getSignOutTransitionFlag } from '@/auth/sessionStorage';
import { isPublicSitePageEnabled } from '@/publicSite/publicSiteConfig';
import { themeModeToggleEnabled } from '@/theme/themeModeFlags';
import { fontFamilies, s, typeScale, typographyTokens } from '@/theme/tokens';
import { Box, Inline, Text } from '@/ui';
import { BreadcrumbsProvider } from '@/ui/layout/BreadcrumbsProvider';
import { PublicShell, type PublicFooterLinkGroup } from '@/ui/layout/PublicShell';
import { Button } from '@/ui/primitives';
import { AuthenticatedLayout } from '../layouts/AuthenticatedLayout';
import { isPublicNavLinkActive, shouldShowPublicAuthActions } from './appShellRouterUtils';
import type { AdminAccessState, AppBootstrapControllerState } from './AppBootstrapController';
import type { AuthError, AuthSession } from '@/auth/types';
import type { NavItem } from '@/components/sidebarTypes';
import type { PlanAlert, PlanBadge } from '@/shared/plans/planStatus';
import type {
  OrganisationDto,
  OrganisationEntitlementsDto,
  PlanCode,
  ProjectDto,
} from '@flowqa/shared';

type AppShellRouterProps = {
  state: AppBootstrapControllerState;
  session: AuthSession | null;
  authError: AuthError | null;
  adminAccess: AdminAccessState;
  isSuperuser: boolean;
  navItems: NavItem[];
  projects: Array<
    Pick<
      ProjectDto,
      'id' | 'name' | 'slug' | 'organisationId' | 'badgeChars' | 'badgeColor' | 'badgeEnabled'
    > & {
      locked: boolean;
      lockReason: string | null;
    }
  >;
  allProjects: Array<
    Pick<
      ProjectDto,
      'id' | 'name' | 'slug' | 'organisationId' | 'badgeChars' | 'badgeColor' | 'badgeEnabled'
    > & {
      locked: boolean;
      lockReason: string | null;
    }
  >;
  organisations: Array<Pick<OrganisationDto, 'id' | 'name' | 'slug'>>;
  canCreateProject: boolean;
  planName: string | null;
  planCode: PlanCode | null;
  planAlerts: PlanAlert[];
  planBadge: PlanBadge | null;
  uploadBlockedReason: string | null;
  addOns: OrganisationEntitlementsDto['addOns'] | null;
  lockProjectSelector: boolean;
  onAddProject: () => void;
  onAddOrganisation: () => void;
  onOpenFeedback: () => void;
  onSignOut: () => Promise<void>;
};

const publicNavLinkSx = {
  fontSize: `${typeScale.lg}px`,
} as const;

export const AppShellRouter = ({
  state,
  session,
  authError,
  adminAccess,
  isSuperuser,
  navItems,
  projects,
  allProjects,
  organisations,
  canCreateProject,
  planName,
  planCode,
  planAlerts,
  planBadge,
  uploadBlockedReason,
  addOns,
  lockProjectSelector,
  onAddProject,
  onAddOrganisation,
  onOpenFeedback,
  onSignOut,
}: AppShellRouterProps): ReactElement => {
  const navigate = useNavigate();
  const handleManualSignOut = useCallback((): void => {
    void onSignOut();
    void navigate('/sign-in', { replace: true, state: { manualSignOut: true } });
  }, [navigate, onSignOut]);

  const titleContent = (
    <Box
      sx={{
        color: 'inherit',
        textDecoration: 'none',
        display: 'inline-flex',
        alignItems: 'center',
        gap: s('sm'),
        paddingY: s('sm'),
      }}
    >
      <Box
        component="img"
        src="/logo.png"
        alt=""
        sx={{ height: '32px', width: '32px', display: 'block' }}
      />
      <Text
        role="sectionTitle"
        component="span"
        sx={{
          fontFamily: fontFamilies.body,
          fontSize: 21,
          lineHeight: 1,
          fontWeight: typographyTokens.bodyStrong.fontWeight,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
        }}
      >
        FlowQA
      </Text>
    </Box>
  );
  const unauthTitle = (
    <Box component={RouterLink} to="/" sx={{ textDecoration: 'none', color: 'inherit' }}>
      {titleContent}
    </Box>
  );
  const showPublicAuthActions = shouldShowPublicAuthActions(
    state.activePath,
    state.isEntryAuthFlow,
  );
  const publicNavLinks = [
    { label: 'Features', to: '/features' },
    ...(isPublicSitePageEnabled('useCases') ? [{ label: 'Use Cases', to: '/use-cases' }] : []),
    ...(isPublicSitePageEnabled('docs') ? [{ label: 'Docs', to: '/docs' }] : []),
    ...(isPublicSitePageEnabled('plans') ? [{ label: 'Plans', to: '/plans' }] : []),
    { label: 'Preview', to: '/preview' },
  ];
  const footerLinkGroups: PublicFooterLinkGroup[] = [
    {
      title: 'Product',
      links: [
        { label: 'Home', href: '/' },
        ...publicNavLinks.map((item) => ({ label: item.label, href: item.to })),
      ],
    },
  ];
  const showEntryAuthThemeToggle =
    themeModeToggleEnabled &&
    (state.activePath.startsWith('/sign-in') || state.activePath.startsWith('/sign-up'));
  const signOutTransition = getSignOutTransitionFlag();

  return (
    <BreadcrumbsProvider items={state.breadcrumbItems} visible={state.showBreadcrumbs}>
      {state.showAuthenticatedShell ? (
        <AuthenticatedLayout
          session={session}
          isAdmin={adminAccess.canAccess}
          isSuperuser={isSuperuser}
          navItems={navItems}
          sidebarFeatureFlags={state.sidebarFeatureFlags}
          hideSidebar={state.onboardingIncomplete || state.activePath === '/onboarding'}
          showSidebarWhenSignedOut={false}
          forceShowSignOut={Boolean(authError) || (!session && state.showAuthenticatedShell)}
          restrictTopBarToSignOut={state.restrictTopBarToSignOut || signOutTransition}
          disableTopBarSignOut={signOutTransition}
          activePath={state.activePath}
          projects={projects}
          allProjects={allProjects}
          organisations={organisations}
          canCreateProject={canCreateProject}
          planName={planName}
          planCode={planCode}
          planAlerts={planAlerts}
          planBadge={planBadge}
          uploadBlockedReason={uploadBlockedReason}
          addOns={addOns}
          lockProjectSelector={lockProjectSelector}
          onNavigate={(path) => {
            void navigate(path);
          }}
          onAddProject={onAddProject}
          onAddOrganisation={onAddOrganisation}
          onOpenFeedback={onOpenFeedback}
          onSignOut={handleManualSignOut}
        >
          {state.routes}
        </AuthenticatedLayout>
      ) : (
        <PublicShell
          title={unauthTitle}
          footerLinkGroups={footerLinkGroups}
          hideFooter={state.activePath === '/preview' || state.isEntryAuthFlow}
          pinFooterToViewport={state.activePath !== '/'}
          actions={
            showPublicAuthActions ? (
              <Inline gap="md" align="center" wrap="nowrap" sx={{ width: 'auto', pr: s('sm') }}>
                {publicNavLinks.map((item) => {
                  const active = isPublicNavLinkActive(state.activePath, item.to);

                  return (
                    <Box
                      key={item.to}
                      sx={{ display: { xs: 'none', md: 'block' }, marginRight: s('sm') }}
                    >
                      <Box
                        component={RouterLink}
                        to={item.to}
                        aria-current={active ? 'page' : undefined}
                        sx={{
                          color: active ? 'text.primary' : 'text.secondary',
                          textDecoration: active ? 'underline' : 'none',
                          textUnderlineOffset: s('sm'),
                          '&:hover': {
                            color: 'text.primary',
                            textDecoration: 'underline',
                          },
                        }}
                      >
                        <Text role="body" component="span" sx={publicNavLinkSx}>
                          {item.label}
                        </Text>
                      </Box>
                    </Box>
                  );
                })}
                <Button
                  component={RouterLink}
                  to="/sign-in"
                  variant="primary"
                  fullWidthOnMobile={false}
                  sx={{
                    display: {
                      xs: 'none',
                      sm: 'inline-flex',
                    },
                  }}
                >
                  Sign In
                </Button>
                <Button
                  component={RouterLink}
                  to="/sign-up"
                  variant="secondary"
                  fullWidthOnMobile={false}
                >
                  Sign Up
                </Button>
              </Inline>
            ) : showEntryAuthThemeToggle ? (
              <Inline gap="none" align="center" wrap="nowrap" sx={{ width: 'auto', pr: s('sm') }}>
                {null}
              </Inline>
            ) : null
          }
        >
          {state.routes}
        </PublicShell>
      )}
    </BreadcrumbsProvider>
  );
};
